import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {useSubheader} from "../../../_metronic/layout/_core/MetronicSubheader";

//helpers:
import {useTranslation} from "react-i18next";
import {getDefaultInsightByPreference, getFeaturesRecursively} from "../../../_metronic/_helpers";

//logic:
import {getItemsSelectorModalData, getNetgraphType, handleTagManager} from "./trackPageLogic";

//actions:
import {trackPageActions} from "../../redux/TrackPage/trackPageRedux";
import {queryActions} from "../../redux/Query/queryRedux";
import {errorActions} from "../../redux/ErrorHandler/errorHandler";
import {setHeaderImg, setIsTrackPage, setLogo, setTrackName} from "../../redux/Layout/layoutSlice";
import {loadParameter, loadUserParameter} from "../../redux_slices/contextSlice";

//components:
import {ItemsSelectorModal} from "../../widgets";
import VideosSearchResults from "../SearchResults";
import NetgraphNavbar from "./Netgraph/NetgraphNavbar";
import TrackPageNetgraph from "./Netgraph/TrackPageNetgraph";
import DetailsCarousels from "./DetailsCarousels";
import {connectStateResults} from "react-instantsearch-core";

//material:
import {CircularProgress, Fade} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import {sendDataLayer} from "../../util/tagmanager";

const TrackPage = connectStateResults(props => {
    const {searchState, match} = props;
    const query = searchState?.query;

    const {t} = useTranslation();

    const subheader = useSubheader();

    const trackId = match.params.level_id;

    //store:
    const language = useSelector(state => loadUserParameter(state, "language"));
    const homePic = useSelector(state => loadParameter(state, "homePic"));
    const corporativeLogo = useSelector(state => loadParameter(state, "corporativeLogo"));

    const {isLoading, showResults} = useSelector(state => state.query);
    const trackInfo = useSelector(state => state.trackPage.trackInfo);

    const dropdown1 = useSelector(state => state.trackPage.dropdown1);
    const dropdown2 = useSelector(state => state.trackPage.dropdown2);
    const dropdown3 = useSelector(state => state.trackPage.dropdown3);

    const errorMessage = useSelector(state => state.error.errorMessage);

    const DROPDOWN1_TYPES = {
        topics: {id: "topics", name: t("TRACKPAGE.GRAPH.NAV.DROPDOWN1.TOPICS")},
        videos: {id: "videos", name: t("TRACKPAGE.GRAPH.NAV.DROPDOWN1.VIDEOS")},
        speakers: {id: "speakers", name: t("TRACKPAGE.GRAPH.NAV.DROPDOWN1.SPEAKERS")},
        highlights: {id: "highlights", name: t("TRACKPAGE.GRAPH.NAV.DROPDOWN1.HIGHLIGHTS")},
    };

    //actions:
    const dispatch = useDispatch();
    const contextSelector = useSelector(state => state.context);
    const getTrackInfo = level_id => dispatch(trackPageActions.getTrackInfo(level_id));
    const getNetgraphData = ({dropdown1, dropdown2, dropdown3}) =>
        dispatch(
            trackPageActions.getNetgraphData({
                trackId,
                dropdown1,
                dropdown2,
                dropdown3,
                language: language || "en-US",
            })
        );
    const getNetgraphConfig = type => dispatch(trackPageActions.getNetgraphConfig(type));
    const clearTrackPage = () => dispatch(trackPageActions.clearTrackPage());
    const clearDetails = () => dispatch(trackPageActions.clearDetails());
    const clearSearcher = () => dispatch(queryActions.clearSearcher());
    const setErrorMessage = msg => dispatch(errorActions.setErrorMessage(msg));

    //DROPDOWN2 OPTIONS:
    const [topicsOptions, setTopicsOptions] = useState([]);
    const [videosOptions, setVideosOptions] = useState([]);
    const [speakersOptions, setSpeakersOptions] = useState([]);

    //DROPDOWN3 OPTIONS:
    const [insightsOptions, setInsightsOptions] = useState([]);

    //SELECTED options:
    //filter1:
    const [filter1Selected, setFilter1Selected] = useState([]);
    //filter2:
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [selectedSpeakers, setSelectedSpeakers] = useState([]);
    const [showSelectorModal, setShowSelectorModal] = useState(false);
    //filter3:
    const [filter3Selected, setFilter3Selected] = useState([]);

    //SELECTED node:
    const [selectedNode, setSelectedNode] = useState(null);
    const [translatedDropdown1Options, setTranslatedDropdown1Options] = useState(null);

    useEffect(() => {
        const trackShowsHighlightsByDefault = trackInfo?.showHighlights;
        const defaultSelectedDropdown1 = trackShowsHighlightsByDefault
            ? DROPDOWN1_TYPES.highlights.id
            : DROPDOWN1_TYPES.topics.id;

        getTrackInfo(trackId);
        getNetgraphData({dropdown1: defaultSelectedDropdown1, dropdown2: null, dropdown3: null});
        getNetgraphConfig(1);
        dispatch(setIsTrackPage(true));

        return () => {
            setErrorMessage("");
            clearSearcher();
            clearTrackPage();
            dispatch(setHeaderImg(homePic));
            dispatch(setLogo(corporativeLogo));
            dispatch(setIsTrackPage(false));
            dispatch(setTrackName(""));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        clearTrackPage();

        const filter1Id = filter1Selected?.[0]?.id || DROPDOWN1_TYPES.topics.id;

        getTrackInfo(trackId);
        getNetgraphData({dropdown1: filter1Id, dropdown2: null, dropdown3: null});
        getNetgraphConfig(1);
        setFilter1Selected([]);
        setVideosOptions([]);
        setSelectedVideos([]);
        setSpeakersOptions([]);
        setSelectedSpeakers([]);
        setTopicsOptions([]);
        setSelectedTopics([]);
    }, [trackId]);

    useEffect(() => {
        const trackName = trackInfo?.name;
        if (trackName) {
            sendDataLayer(
                {track_id: trackId, track_name: trackName, from: document.referrer, event: "track_visited"},
                {context: contextSelector}
            );
        }
    }, [trackInfo]);

    useEffect(() => {
        if (!showResults) {
            const trackName = trackInfo?.name;

            if (trackName) {
                subheader.setTitle(`${trackName}`);
                dispatch(setHeaderImg(trackInfo.header || "/media/bg/bg-13.PNG"));
                dispatch(setTrackName(trackName));
            }
        } else {
            const pageTitle = t("SEARCH_RESULTS_PAGE.TITLE");
            subheader.setTitle(pageTitle);
        }
    }, [trackInfo, showResults]);

    useEffect(() => {
        const hasDropdown1Data = !!dropdown1?.[0]?.id;
        const isFilter1Selected = !!filter1Selected?.[0]?.id;

        let translatedOptions = [];
        if (hasDropdown1Data) {
            translatedOptions = dropdown1.map(option => {
                const nameToUpperCase = option?.name ? option.name.toUpperCase() : "";
                const translationKey = nameToUpperCase ? `TRACKPAGE.GRAPH.NAV.DROPDOWN1.${nameToUpperCase}` : "";
                const translatedName = t(translationKey);

                return {...option, translatedName: translatedName || option.name};
            });
        }
        setTranslatedDropdown1Options(translatedOptions);

        if (hasDropdown1Data && !isFilter1Selected) {
            setFilter1Selected([translatedOptions[0]]);
        }
    }, [dropdown1]);

    useEffect(() => {
        const selectedFilter1ID = filter1Selected?.[0]?.id;

        if (selectedFilter1ID) {
            const netpgrahType = getNetgraphType(DROPDOWN1_TYPES, selectedFilter1ID);

            getNetgraphData({dropdown1: selectedFilter1ID, dropdown2: null, dropdown3: null});
            getNetgraphConfig(netpgrahType);
        }
    }, [filter1Selected]);

    const handleItemSelected = async (itemsSelected, debounce) => {
        const setSelection = handleGetItemsSelectorModal().setSelectedItems;
        setSelection(itemsSelected);
        const filter1ID = filter1Selected[0].id;
        const arrayOfIds = itemsSelected.map(item => item?.id);
        return getNetgraphData({dropdown1: filter1ID, dropdown2: arrayOfIds, dropdown3: null});
    };

    useEffect(() => {
        const hasDropdown2Data = dropdown2?.[0]?.id;

        if (hasDropdown2Data) {
            const filter1ID = filter1Selected?.[0]?.id;
            if (filter1ID) {
                if (filter1ID === DROPDOWN1_TYPES.videos.id) {
                    setVideosOptions(dropdown2);
                    handleItemSelected([dropdown2[0]]);
                } else if (filter1ID === DROPDOWN1_TYPES.speakers.id) {
                    setSpeakersOptions(dropdown2);
                    handleItemSelected([dropdown2[0]]);
                } else if (filter1ID === DROPDOWN1_TYPES.topics.id) {
                    setTopicsOptions(dropdown2);
                    handleItemSelected([dropdown2[0]]);
                }
            }
        } else {
            setVideosOptions([]);
            setSelectedVideos([]);
            setSpeakersOptions([]);
            setSelectedSpeakers([]);
            setTopicsOptions([]);
            setSelectedTopics([]);
        }
    }, [dropdown2]);

    useEffect(() => {
        if (dropdown3 && dropdown3[0]) {
            const allAvailableInsights = getFeaturesRecursively(dropdown3, true);
            setInsightsOptions(allAvailableInsights);

            const selectedInsightWithPreference = [getDefaultInsightByPreference(allAvailableInsights)];
            setFilter3Selected(selectedInsightWithPreference);
        } else {
            setInsightsOptions([]);
            setFilter3Selected([]);
        }
    }, [dropdown3]);

    useEffect(() => {
        clearDetails();
    }, [filter3Selected]);

    const handleGetItemsSelectorModal = () => {
        return getItemsSelectorModalData(
            DROPDOWN1_TYPES,
            filter1Selected,
            videosOptions,
            selectedVideos,
            setSelectedVideos,
            speakersOptions,
            selectedSpeakers,
            setSelectedSpeakers,
            topicsOptions,
            selectedTopics,
            setSelectedTopics
        );
    };

    const [oldSelection, setOldSelection] = useState({});
    useEffect(() => {
        if (filter1Selected) {
            handleTagManager({
                track_id: trackId,
                track_name: trackInfo?.name,
                DROPDOWN1_TYPES,
                filter1Selected,
                selectedVideos,
                selectedSpeakers,
                selectedTopics,
                filter3Selected,
                contextSelector,
                oldSelection,
                setOldSelection,
            });
        }
    }, [selectedVideos, selectedSpeakers, selectedTopics, filter3Selected, trackInfo?.name]);

    if (isLoading)
        return (
            <div
                className="col-12 d-flex justify-content-center align-items-center"
                style={{minHeight: "40vh", opacity: 1}}
            >
                <CircularProgress />
            </div>
        );

    if (errorMessage && !isLoading)
        return (
            <div className="col-12 d-flex justify-content-center align-items-center py-10">
                <div className="alert alert-light text-dark w-fit-content">
                    <WarningRoundedIcon color="inherit" className="mr-5" />
                    {errorMessage}
                </div>
            </div>
        );

    return (
        <div className={'d-flex flex-column w-100'}>
            <div className="trackPage container">
                <div className="d-flex min-h-90 w-100 justify-content-center align-items-center">
                    <Fade
                        in={!query}
                        mountOnEnter={true}
                        unmountOnExit={true}
                        timeout={{
                            exit: 0,
                            appear: 1000,
                            enter: 1000,
                        }}
                    >
                        <div className={`trackExplore ${query ? "d-none" : "d-flex"}`}>
                            {/* NETGRAPH MENU: */}
                            <NetgraphNavbar
                                filter1Selected={filter1Selected}
                                setFilter1Selected={setFilter1Selected}
                                dropdown1={translatedDropdown1Options}
                                showSelectorModal={showSelectorModal}
                                setShowSelectorModal={setShowSelectorModal}
                                dropdown2Type={handleGetItemsSelectorModal().type}
                                selectedItemsDropdown2={handleGetItemsSelectorModal().selectedItems}
                                insightsOptions={insightsOptions}
                                selectedInsights={filter3Selected}
                                setSelectedInsights={setFilter3Selected}
                            />
                            {/* NETGRAPH: */}
                            <TrackPageNetgraph
                                trackId={trackId}
                                trackName={trackInfo?.name}
                                setSelectedNode={setSelectedNode}
                                selectedInsights={filter3Selected}
                                selectedNode={selectedNode}
                                filter1Selected={filter1Selected}
                                selectedVideos={selectedVideos}
                                selectedSpeakers={selectedSpeakers}
                                selectedTopics={selectedTopics}
                            />
                            {/* ///////////////////////////////////////////////////////////////////// */}

                            <DetailsCarousels
                                selectedNode={selectedNode}
                                filter1Selected={filter1Selected}
                                trackInfo={trackInfo}
                                selectedInsights={filter3Selected}
                            />
                        </div>
                    </Fade>
                </div>
            </div>
            <VideosSearchResults />
            <ItemsSelectorModal
                type={handleGetItemsSelectorModal().type}
                showModal={showSelectorModal}
                setShowModal={setShowSelectorModal}
                allItems={handleGetItemsSelectorModal().allItems}
                selectedItems={handleGetItemsSelectorModal().selectedItems}
                handleItemSelected={handleItemSelected}
            />
        </div>
    );
});

TrackPage.propTypes = {
    match: PropTypes.object,
};

export default TrackPage;
